// @givmo/app-admin — the waffle's squares (EP-SUITE3; shell contract v3 at OM-14).
//
// Turns the shared {@link MODULE_REGISTRY} into the popup's two sections through the package's ONE
// square derivation (`deriveConsoleSquares`). The registry file is duplicated byte-for-byte from
// `apps/company` (apps here never import each other) and a test in each app pins the two identical —
// so the portal's popup and the console's own popup can never offer different app sets. The rule is
// shared more strongly still: it lives once in `@givmo/org-console` and both apps import it.
//
// The only difference from the console's copy is WHICH square is current: here the EMPLOYER portal
// is the app you are in, so it renders at `/`. Everything else — the URL-set gate, the registry
// order, the state rule — is identical.
//
// ⚠ HOME IS NOT A THING (D-CPO-31, R2). The role-aware suite-home square is GONE. What replaced it is
// the ADMIN CONSOLE tile — the organization's own management pages on the console origin — rendered
// LAST and badged for whoever holds one of its pages' capabilities (never `isGlobalAdmin`: the build
// law), composed from `VITE_COMPANY_CONSOLE_URL` + `/org`. Unset ⇒ no tile, never a guessed host.
// The same absence removes the "Turn on" affordance from a switched-off tile: with no console
// origin there is nowhere to send someone, so the tile states the fact instead.
//
// ⚠ THE STANDING READ IS `GET /admin/company/me`, WHICH THIS PORTAL ALREADY MOUNTS (the shell, the
// Admin Team page, the settings tabs all use `companiesGetCompanyAdminOptions()`), so the waffle
// costs no extra request. It is also NOT gated on `COMPANY_CONSOLE_ENABLED` — `product_access` and
// the v3 read-level fields landed additively on an ungated route — so the state rule is correct in
// an environment where the console's own routes still 404.
//
// ⚠ NO TILE OPTIONS MENU — here or anywhere (RULED 2026-10-01 07:02 PDT): the default landing page
// is each person's own setting, on the console's settings page.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { companiesGetCompanyAdminOptions } from '@givmo/api-client/query';
import { Icon, type ModuleSquareProps } from '@givmo/design-system';
import {
  CAPABILITY_WRITE_SETTINGS,
  deriveConsoleSquares,
  hasOtherModules,
  holdsCapability,
  moduleTurnOnHref,
  standingReadFrom,
  standingsFromAccessBag,
  type ConsoleSquare,
  type ModuleStandings,
} from '@givmo/org-console';
import { joinPath, readUrl, registryTable, type RegistryRow } from './module-registry';

/** The organization's own page inside the console — where "Turn on" and "Setting up" go. */
const CONSOLE_ORG_PATH = '/org';
/** The console ORIGIN variable the registry pair reads. */
const CONSOLE_URL_VAR = 'VITE_COMPANY_CONSOLE_URL';
/** This portal IS the employer module; its own square is the current one, at the in-app root. */
const CURRENT_SLUG = 'employer';

export interface PortalModules {
  /** The apps this person can OPEN — the popup's first section, the Admin Console tile last. */
  mine: ModuleSquareProps[];
  /** Every other app offered to the organization's class — the popup's second section. */
  more: ModuleSquareProps[];
  /** At least one OPENABLE square that is not this portal (the Admin Console tile counts) — the waffle's "somewhere to go" gate. */
  hasOtherApps: boolean;
  /**
   * The standing read has not settled yet.
   *
   * ⚠ CALLERS MUST RENDER NOTHING WHILE THIS IS TRUE. Until the read lands there is no standing to
   * apply, so every tile reads as an ordinary link (the not-reported posture) — which means a waffle
   * rendered during that window can DISAPPEAR a moment later for an admin who holds nothing else. A
   * control that vanishes under someone reads as a bug; one that fills in reads as loading.
   */
  isPending: boolean;
}

export interface PortalTileContext {
  /** The caller holds `write_settings` — the Turn On door's capability (never a tier check). */
  canTurnOn: boolean;
  /** The console's `/org` on its configured origin, or `null` when the origin is unset. */
  managementHref: string | null;
  /** Hook modules whose module-owned activation transport this host actually mounted. */
  activationHandlers: readonly string[];
  standings: ModuleStandings | null;
}

/**
 * One square as the popup renders it. A locked or stated-off square carries NO request door here:
 * the request dialog is the console's (it writes the console's `/org` family); this portal offers
 * the statement and the way there is the Admin Console tile.
 */
export function portalTile(
  square: ConsoleSquare<RegistryRow>,
  context: PortalTileContext,
): ModuleSquareProps {
  const { row } = square;
  const icon = <Icon icon={row.icon} size={22} aria-hidden />;
  const base = { slug: row.slug, label: row.label, icon };
  if (square.verdict === 'link' && square.destination !== null) {
    if (square.kind === 'admin-console') {
      return {
        ...base,
        href: square.destination.href,
        adminConsole: true,
        ...(square.badge === undefined ? {} : { badge: square.badge }),
      };
    }
    return { ...base, href: square.destination.href, current: square.current };
  }
  // OM-33: an announced module this company is not offered. Nothing is announced to companies,
  // and `/admin/company/me` carries no list, so this is the shared shape rather than a live case.
  if (square.verdict === 'coming-soon') return { ...base, state: 'coming-soon' };
  if (square.verdict === 'locked') return { ...base, state: 'locked' };
  const turnOnHref =
    context.canTurnOn && context.managementHref !== null
      ? moduleTurnOnHref(row.slug, context.managementHref, context.activationHandlers)
      : null;
  if (square.verdict === 'off') {
    return turnOnHref === null
      ? { ...base, state: 'off' }
      : { ...base, state: 'off', turnOn: true, href: turnOnHref };
  }
  return context.canTurnOn && context.managementHref !== null
    ? { ...base, state: 'setting-up', viewSetup: true, href: context.managementHref }
    : { ...base, state: 'setting-up' };
}

export interface PortalModulesOptions {
  /** Hook modules whose module-owned activation transport this host actually mounted. */
  activationHandlers?: readonly string[];
}

const NO_ACTIVATION_HANDLERS: readonly string[] = [];

/** The popup's squares, and whether the standing read that shapes them has settled. */
export function usePortalModules(options: PortalModulesOptions = {}): PortalModules {
  const { activationHandlers = NO_ACTIVATION_HANDLERS } = options;
  const { data: me, isPending } = useQuery(companiesGetCompanyAdminOptions());
  const standings = standingsFromAccessBag(me?.product_access);
  const read = standingReadFrom(me);
  const canTurnOn = holdsCapability(read.capabilities, CAPABILITY_WRITE_SETTINGS);

  return useMemo(() => {
    const env = import.meta.env;
    const consoleOrigin = readUrl(env, CONSOLE_URL_VAR) ?? null;
    // This portal's own square is the current one, at its in-app root — a plain anchor, exactly as
    // before. Every other square's destination is its variable, verbatim; the Admin Console's is the
    // console origin plus `/org`, composed by the registry.
    const table = registryTable(env, consoleOrigin).map((row) =>
      row.slug === CURRENT_SLUG ? { ...row, href: '/' } : row,
    );
    const squares = deriveConsoleSquares(table, {
      standings,
      capabilities: read.capabilities,
      origin: window.location.origin,
      currentSlug: CURRENT_SLUG,
      pendingAccessRequests: read.pendingAccessRequests,
      comingSoon: read.comingSoonModules,
    });
    const context: PortalTileContext = {
      canTurnOn,
      managementHref: consoleOrigin === null ? null : joinPath(consoleOrigin, CONSOLE_ORG_PATH),
      activationHandlers,
      standings,
    };
    const mine: ModuleSquareProps[] = [];
    const more: ModuleSquareProps[] = [];
    for (const square of squares) {
      (square.verdict === 'link' ? mine : more).push(portalTile(square, context));
    }
    // ⚠ THE ADMIN CONSOLE TILE COUNTS HERE, and only here. The package's rule leaves it out because
    // a console with nothing openable must not read as having an app (the `/apps` statement's
    // posture). This portal is a different property: a person standing in it who holds an Admin
    // Console capability has somewhere to go — the console's `/org` — and the tile in this popup is
    // their only way there.
    const hasOtherApps =
      hasOtherModules([...mine, ...more]) || mine.some((tile) => tile.adminConsole === true);
    return { mine, more, hasOtherApps, isPending };
  }, [
    standings,
    read.capabilities,
    read.pendingAccessRequests,
    canTurnOn,
    activationHandlers,
    isPending,
  ]);
}
